import type {
  SensitivityAnalysisResult,
  SensitivityFeatureKey,
  SensitivityRoundResult,
} from '@aloft/toolkit-shared';
import { AloftPrice, FEATURE_RELIABILITY_THRESHOLDS } from '@aloft/toolkit-shared';
import { Flex, Typography, colors } from '@aloft/toolkit-sui';

import { resolveSensitivitySelectedRate } from '../../../components/PropertyGrid/columnSections/sensitivitySelectedRate.ts';
import { SensitivityLineGraph } from '../../../components/graphs/sensitivity/SensitivityLineGraph.tsx';

const featureRateXLabel: Record<SensitivityFeatureKey, string> = {
  gla: 'GLA Unit Rate',
  bedrooms: 'Bedrooms Unit Rate',
  fullBaths: 'Full Baths Unit Rate',
  halfBaths: 'Half Baths Unit Rate',
  garageCarport: 'Garage/Carport Unit Rate',
  siteSize: 'Site Size Unit Rate',
  quality: 'Quality Unit Rate',
  condition: 'Condition Unit Rate',
};

type LegendItemProps = {
  color: string;
  label: string;
  line?: boolean;
};

const LegendItem = ({ color, label, line }: LegendItemProps) => (
  <Flex alignItems="center" gap="0.25rem">
    <div
      style={{
        width: 8,
        height: line ? 1 : 4,
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
    <span style={{ color, fontSize: 10, fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  </Flex>
);

type SensitivityModalGraphSectionProps = {
  result: SensitivityAnalysisResult;
  featureKey: SensitivityFeatureKey;
  round?: SensitivityRoundResult;
};

export const SensitivityModalGraphSection = ({
  result,
  featureKey,
  round,
}: SensitivityModalGraphSectionProps) => {
  const data = result.testedUnitRates.map((entry) => ({
    rate: AloftPrice.schema.parse(entry.rate),
    spreadRatio: entry.spreadRatio,
  }));
  const selectedRate =
    resolveSensitivitySelectedRate(round, result) ??
    AloftPrice.schema.parse(result.selectedRate.rate);
  const xLabel = featureRateXLabel[featureKey];
  const { absoluteThreshold, conditionalZoneMax } = FEATURE_RELIABILITY_THRESHOLDS[featureKey];

  return (
    <Flex
      flexDirection="column"
      backgroundColor="graphBackground"
      borderRadius="8px"
      padding="0.75rem"
      gap="0.5rem"
      flexShrink={0}
      width="100%"
    >
      <Flex alignItems="center" gap="1rem" flexWrap="wrap">
        <Typography variant="h3">Percent Variance Curve</Typography>
        <Typography variant="body2" color="textSecondary">
          Shows percent variance across all tested adjustment rates
        </Typography>
        <Flex alignItems="center" gap="0.5rem" flexWrap="wrap">
          <LegendItem color={colors.graphAdverse} label="Percent Variance" line />
          <LegendItem color={colors.graphBeneficial} label="Excellent" />
          <LegendItem color={colors.graphChart} label="Good" />
          <LegendItem color={colors.graphChart4} label="Acceptable" />
          <LegendItem color={colors.graphChart3} label="Unreliable" />
        </Flex>
      </Flex>

      <SensitivityLineGraph
        data={data}
        selectedRate={selectedRate}
        xLabel={xLabel}
        absoluteThreshold={absoluteThreshold}
        conditionalZoneMax={conditionalZoneMax}
        disableAnimations={false}
        height={200}
      />
    </Flex>
  );
};
